import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/library/dropdown-menu";
import { Badge } from "@/components/ui/others/Badges";
import { course_spesific_key } from "@/config/endpoints";
import useSpesificCustoms from "@/features/customField/api/useSpesificCustoms";
import { getCfDisplayName } from "@/features/customField/utils/cfDisplayName";
import { useCourseForm } from "@/features/universityAndCourse/contexts/FormCourseContext";
import { ChevronDown } from "lucide-react";
import { Controller } from "react-hook-form";
import {
  getCustomInputScopeErrorMessage,
  hasCustomInputScopeError,
} from "@/utils/formErrors";

type IntakeField = {
  id: number | string;
  name: string;
  options?: { id: string | number; value: string }[];
};

/**
 * Picker for ONE intake-related course CF (e.g. "Intake Month" OR "Intake
 * Year"). Owns its own search state. The parent <IntakeSelector /> renders
 * one of these per matching CF so multiple intake CFs co-exist without
 * stomping on each other's `customInputScopes[index]` slot.
 */
function IntakeFieldSelector({
  field,
  index,
}: {
  field: IntakeField;
  index: number;
}) {
  const { control, errors } = useCourseForm();
  const [search, setSearch] = useState("");

  const scopeErr = errors?.customInputScopes?.[index];
  const scopeMsg = getCustomInputScopeErrorMessage(scopeErr);
  const scopeInvalid = hasCustomInputScopeError(scopeErr);

  const sortedOptions = useMemo(() => {
    if (!field.options?.length) return [];
    return [...field.options].sort((a, b) =>
      String(a.value).localeCompare(String(b.value), undefined, {
        sensitivity: "base",
        numeric: true,
      }),
    );
  }, [field.options]);

  const filteredOptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sortedOptions;
    return sortedOptions.filter((op) =>
      String(op.value).toLowerCase().includes(q),
    );
  }, [sortedOptions, search]);

  return (
    <div className="flex flex-col gap-1.5 w-full">
      <Label className={scopeInvalid ? "text-red-500" : ""}>
        {getCfDisplayName(field)}
      </Label>

      <Controller
        name={`customInputScopes.${index}`}
        control={control}
        defaultValue={{
          customInputId: field.id,
          optionIds: [],
        }}
        render={({ field: rhfField }) => {
          const selectedValues: string[] = Array.isArray(rhfField.value?.optionIds)
            ? rhfField.value.optionIds
            : [];

          const toggleValue = (value: string) => {
            const updated = selectedValues.includes(value)
              ? selectedValues.filter((v) => v !== value)
              : [...selectedValues, value];

            rhfField.onChange({
              customInputId: field.id,
              optionIds: updated,
            });
          };

          const selectedOptions = (field.options ?? []).filter((op) =>
            selectedValues.includes(String(op.id)),
          );

          return (
            <>
              <DropdownMenu
                onOpenChange={(open) => {
                  if (!open) setSearch("");
                }}
              >
                <DropdownMenuTrigger asChild className="w-full">
                  <button
                    type="button"
                    className={`w-full flex items-center justify-between rounded-2xl border bg-background px-3 py-2 text-sm transition-all focus:outline-none ${
                      scopeInvalid ? "border-red-500" : "border-input"
                    }`}
                  >
                    <span className="text-muted-foreground text-left">
                      {selectedOptions.length
                        ? `${selectedOptions.length} selected`
                        : `Select ${getCfDisplayName(field).toLowerCase()}`}
                    </span>
                    <ChevronDown className="h-4 w-4 opacity-50 shrink-0" />
                  </button>
                </DropdownMenuTrigger>

                <DropdownMenuContent
                  align="start"
                  sideOffset={4}
                  className="w-[var(--radix-dropdown-menu-trigger-width)] min-w-[var(--radix-dropdown-menu-trigger-width)] max-w-[var(--radix-dropdown-menu-trigger-width)] bg-white rounded-xl shadow-xl border p-1 z-[50] max-h-[min(70vh,320px)] flex flex-col overflow-hidden"
                >
                  <div
                    className="p-1 pb-2 border-b border-[#e6e8e7] sticky top-0 bg-white"
                    onKeyDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Input
                      type="search"
                      placeholder={`Search ${getCfDisplayName(field).toLowerCase()}…`}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="h-9 bg-[#fcfcfc] text-sm"
                      autoComplete="off"
                    />
                  </div>
                  <div className="overflow-y-auto py-1 min-h-0 max-h-56">
                    {filteredOptions.length === 0 ? (
                      <p className="px-2 py-3 text-sm text-muted-foreground text-center">
                        No options match your search
                      </p>
                    ) : (
                      filteredOptions.map((op) => (
                        <DropdownMenuCheckboxItem
                          key={op.id}
                          checked={selectedValues.includes(String(op.id))}
                          onCheckedChange={() => toggleValue(String(op.id))}
                          onSelect={(e) => e.preventDefault()}
                          className="relative flex w-full cursor-pointer select-none items-center rounded-lg py-2.5 pl-10 pr-2 text-sm outline-none focus:bg-slate-100"
                        >
                          <span className="truncate">{op.value}</span>
                        </DropdownMenuCheckboxItem>
                      ))
                    )}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>

              {selectedOptions.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {selectedOptions
                    .slice()
                    .sort((a, b) =>
                      String(a.value).localeCompare(String(b.value), undefined, {
                        sensitivity: "base",
                        numeric: true,
                      }),
                    )
                    .map((op) => (
                      <Badge key={op.id} variant="gray">
                        {op.value}
                      </Badge>
                    ))}
                </div>
              )}
            </>
          );
        }}
      />
      {scopeMsg ? <p className="text-sm text-red-500">{scopeMsg}</p> : null}
    </div>
  );
}

/**
 * Renders one selector per course CF whose name starts with "intake"
 * (case-insensitive). With "Intake Month" + "Intake Year" CFs configured,
 * counsellors get two independent multi-selects writing to distinct
 * `customInputScopes[idx]` slots. CoursePrefrenceRenderer already hides
 * the same CFs via `hideIfNameStartsWith="intake"` so there is no duplicate
 * UI.
 */
export default function IntakeSelector() {
  const { spesificFields } = useSpesificCustoms({
    queryKey: course_spesific_key,
    modelName: "course",
    type: "SELECTOR",
  });

  const intakeFields: { field: IntakeField; index: number }[] = useMemo(() => {
    if (!Array.isArray(spesificFields)) return [];
    return spesificFields
      .map((val: any, index: number) => ({ field: val as IntakeField, index }))
      .filter(({ field }) =>
        typeof field?.name === "string" &&
        field.name.toLowerCase().startsWith("intake"),
      );
  }, [spesificFields]);

  if (intakeFields.length === 0) return null;

  return (
    <div className="flex flex-col gap-3 w-full">
      {intakeFields.map(({ field, index }) => (
        <IntakeFieldSelector key={String(field.id)} field={field} index={index} />
      ))}
    </div>
  );
}
